import { useEffect, useState } from 'react';
import { DateTime, Interval } from 'luxon';
import { Calendar } from '../Common';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { DisabledDaysWithMessage } from '../../commonTypes/acs';
import { HolidayDto } from '../../commonTypes/organization.types';
import { useTranslation } from '../../hooks/useTranslation';
import { LeaveRequestType, LeaveDurationTypeEnum } from '../../commonTypes/leaveRequest.types';
import { IDateRange } from '../../util/date/dateHelper';

export interface IDateRangeValues {
    dates: IDateRange;
    leaveDurationType: LeaveDurationTypeEnum;
    leaveType?: { label: string; value: string | number };
}

interface IDataRangePicker {
    values: IDateRangeValues;
    setFieldValue: (field: string, value: any) => void;
    disabledDays: DisabledDaysWithMessage[];
    onPeriodChange: (from: string, to: string) => void;
    holidays?: HolidayDto[];
    initialDate?: DateTime | null;
    isEndMedicalLeave?: boolean;
    fixedStartDate?: string;
}

const DataRangePicker = ({
    values,
    setFieldValue,
    disabledDays,
    onPeriodChange,
    holidays,
    initialDate,
    isEndMedicalLeave = false,
    fixedStartDate,
}: IDataRangePicker) => {
    const { t } = useTranslation('leaveRequest');
    const { startDate, endDate } = values.dates;
    const [displayDateCalendarLeft, setDisplayDateCalendarLeft] = useState(initialDate ? initialDate.startOf('day') : DateTime.now().startOf('day'));
    const [displayDateCalendarRight, setDisplayDateCalendarRight] = useState(
        initialDate ? initialDate.startOf('day') : DateTime.now().startOf('day')
    );

    const isSecondCalendarActive = !useResizeListener().isMaxWidthMedium;
    const isMedicalLeave = values.leaveType?.value === LeaveRequestType.MEDICAL_LEAVE;

    useEffect(() => {
        onPeriodChange(displayDateCalendarLeft.startOf('month').toISO(), displayDateCalendarLeft.plus({ month: 1 }).endOf('month').toISO());
    }, [displayDateCalendarLeft.toISO(), displayDateCalendarRight.toISO()]);

    const handleDisplayDate = (_changeDate: DateTime, nextBackAction?: string) => {
        if (nextBackAction === 'right') {
            setDisplayDateCalendarLeft(prev => prev.plus({ months: 1 }));
            setDisplayDateCalendarRight(prev => prev.plus({ months: 1 }));
        }

        if (nextBackAction === 'left') {
            setDisplayDateCalendarLeft(prev => prev.minus({ months: 1 }));
            setDisplayDateCalendarRight(prev => prev.minus({ months: 1 }));
        }
    };

    const changeDate = (selectedDate: DateTime) => {
        let existTimelogDayInRange: boolean = false;

        if (isEndMedicalLeave && startDate) {
            if (selectedDate < startDate) {
                return false;
            }
            return setFieldValue('dates.endDate', selectedDate);
        }

        if (startDate !== null && endDate !== null) {
            if (!isMedicalLeave && selectedDate < DateTime.now().startOf('day')) {
                return false;
            }

            return setFieldValue('dates', { startDate: selectedDate, endDate: null });
        }

        if (startDate !== null && endDate === null) {
            const interval = Interval.fromDateTimes(startDate, selectedDate)
                .splitBy({ day: 1 })
                .map(d => d.start);

            const blockedDates = disabledDays.filter((item: DisabledDaysWithMessage) => item.message === t('youHaveMedicalLeaveRequest'));

            blockedDates.forEach((item: DisabledDaysWithMessage) => {
                item.dates.forEach((date: string) => {
                    interval.forEach(betweenDay => {
                        if (betweenDay.day === DateTime.fromISO(date).day && betweenDay.month === DateTime.fromISO(date).month) {
                            existTimelogDayInRange = true;
                        }
                    });
                });
            });

            if (!isMedicalLeave && selectedDate < DateTime.now().startOf('day')) {
                return false;
            }

            if (selectedDate <= startDate) {
                return setFieldValue('dates.startDate', selectedDate);
            }
        }

        if (!isMedicalLeave && selectedDate < DateTime.now().startOf('day')) {
            return false;
        }

        if (startDate && !existTimelogDayInRange) {
            return setFieldValue('dates.endDate', selectedDate);
        }

        setFieldValue('dates.startDate', selectedDate);
    };

    return (
        <div className="data-range-picker">
            <Calendar
                displayDate={displayDateCalendarLeft}
                changeMonth={handleDisplayDate}
                startDate={values.dates.startDate}
                endDate={values.dates.endDate}
                onClick={changeDate}
                activeArrows={{
                    active: true,
                    firstCalendarLeftButton: true,
                    secondCalendarRightButton: !isSecondCalendarActive,
                }}
                disabledDays={disabledDays}
                holidays={holidays}
                isMedicalLeave={isMedicalLeave}
                isEndMedicalLeave={isEndMedicalLeave}
                fixedStartDate={fixedStartDate}
            />
            {isSecondCalendarActive && (
                <Calendar
                    displayDate={displayDateCalendarRight.plus({ month: 1 })}
                    changeMonth={handleDisplayDate}
                    startDate={values.dates.startDate}
                    endDate={values.dates.endDate}
                    onClick={changeDate}
                    activeArrows={{
                        active: true,
                        secondCalendarRightButton: true,
                    }}
                    disabledDays={disabledDays}
                    holidays={holidays}
                    isMedicalLeave={isMedicalLeave}
                    isEndMedicalLeave={isEndMedicalLeave}
                    fixedStartDate={fixedStartDate}
                />
            )}
        </div>
    );
};

export default DataRangePicker;
